import type { ButtonHTMLAttributes } from 'react';
import { Avatar } from './Avatar';

export interface IdentityChipProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /** Display name — drives the avatar's monogram fallback. */
  name: string;
  /** Optional role label (mono, uppercase). */
  role?: string;
  /** Avatar size. Default `'sm'`. */
  size?: 'sm' | 'md';
  /** Flyout-open state — adds `aria-expanded` and the open styling. */
  expanded?: boolean;
}

/**
 * Visual-only identity chip — 36px pill with avatar + name + role + caret.
 * Matches dashboard.html `.identity`. The shell's stateful
 * `src/components/shell/IdentityChip.tsx` continues to own the flyout
 * lifecycle (Skeleton placeholders, useShellSession data); task 0e will
 * refactor it to compose this primitive.
 */
export function IdentityChip({
  name,
  role,
  size = 'sm',
  expanded,
  className,
  type,
  ...rest
}: IdentityChipProps) {
  const cls = ['pn-identity', className].filter(Boolean).join(' ');
  return (
    <button
      type={type ?? 'button'}
      className={cls}
      aria-haspopup="menu"
      aria-expanded={expanded}
      {...rest}
    >
      <Avatar name={name} size={size} />
      <span className="pn-identity-who">
        <span className="pn-identity-name">{name}</span>
        {role ? <span className="pn-identity-role">{role}</span> : null}
      </span>
      <span className="pn-identity-caret" aria-hidden="true">
        ▾
      </span>
    </button>
  );
}
